import React, { useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper,
  Divider,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import ReportCarousel from './ReportCarousel';

const AGENT_PROGRESS_TEAMS = {
  tradingagents: {
    'Analyst Team': ['Market Analyst', 'Social Analyst', 'News Analyst', 'Fundamentals Analyst'],
    'Research Team': ['Bull Researcher', 'Bear Researcher', 'Research Manager'],
    'Trading Team': ['Trader'],
    'Risk Management': ['Risky Analyst', 'Neutral Analyst', 'Safe Analyst'],
    'Portfolio Management': ['Portfolio Manager'],
  },
  research_agent: { 'Research': ['Research Agent'] },
  stock_agent: { 'Stock Analysis': ['Stock Agent'] },
  ta_agent: { 'Technical Analysis': ['TA Agent'] },
  guru_agents: {
    'Guru Investors': [
      'Aswath Damodaran', 'Ben Graham', 'Bill Ackman', 'Cathie Wood',
      'Charlie Munger', 'Michael Burry', 'Mohnish Pabrai', 'Peter Lynch',
      'Phil Fisher', 'Rakesh Jhunjhunwala', 'Stanley Druckenmiller', 'Warren Buffett',
    ],
    'Guru Specialists': [
      'Technical Analyst', 'Fundamentals Analyst', 'Growth Analyst',
      'News Sentiment Analyst', 'Sentiment Analyst', 'Valuation Analyst',
    ],
    'Guru Portfolio': ['Guru Risk Manager', 'Guru Portfolio Manager'],
  },
};

function AnalysisProgress({ config, analysisData, isAnalyzing, sessionId, analysisStatus }) {
  const selectedAgents = config?.selected_agents || Object.keys(AGENT_PROGRESS_TEAMS);

  const agentTeams = {};
  for (const agentKey of selectedAgents) {
    const teams = AGENT_PROGRESS_TEAMS[agentKey];
    if (teams) Object.assign(agentTeams, teams);
  }
  agentTeams['Synthesis'] = ['Aggregate Agent'];
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isCompleted = analysisStatus === "completed";

  const handleSave = async () => {
    if (!sessionId) {
      setSnackbar({ open: true, message: 'No active session to save', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`/api/save/${sessionId}`);
      if (response.data.success) {
        setSnackbar({ 
          open: true, 
          message: `Analysis saved to history (ID: ${response.data.analysis_id})`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ open: true, message: response.data.error || 'Failed to save', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || 'Failed to save analysis', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" color="primary">
          {isCompleted ? 'Analysis Complete' : 'Analysis in Progress'}
        </Typography>
        {isCompleted && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save to History'}
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Ticker"
                    secondary={config?.ticker || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Date"
                    secondary={config?.analysis_date || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Agents"
                    secondary={(config?.selected_agents || []).join(', ') || 'All'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="LLM Provider"
                    secondary={config?.llm_provider || 'N/A'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Team Progress
              </Typography>

              {Object.entries(agentTeams).map(([teamName, agents]) => (
                <Box key={teamName} mb={2}>
                  <Typography variant="subtitle1" color="secondary" gutterBottom>
                    {teamName}
                  </Typography>

                  {agents.map((agent) => {
                    let status = analysisData?.agent_status?.[agent] || 'pending';
                    if (isCompleted && (status === 'in_progress' || status === 'pending')) {
                      status = 'completed';
                    }
                    return (
                      <Box key={agent} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">{agent}</Typography>
                        <Chip
                          label={getStatusText(status)}
                          color={getStatusColor(status)}
                          size="small"
                        />
                      </Box>
                    );
                  })}

                  {teamName !== Object.keys(agentTeams).at(-1) && <Divider sx={{ my: 1 }} />}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <ReportCarousel 
            analysisData={analysisData}
            title="Analysis Reports"
            showScrollHint={false}
          />
        </Grid>

        {analysisData?.messages && analysisData.messages.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Messages
                </Typography>
                <Paper
                  elevation={1}
                  sx={{
                    maxHeight: 450,
                    overflow: 'auto',
                    backgroundColor: 'background.default'
                  }}
                >
                  <List dense>
                    {analysisData.messages.slice(-20).map((msg, index) => {
                      const chipColor = {
                        'Tool Call': 'primary',
                        'Data': 'info',
                        'Reasoning': 'default',
                        'System': 'success',
                        'Error': 'error',
                        'Debate': 'warning',
                        'Input': 'secondary',
                      }[msg.type] || 'default';
                      return (
                        <ListItem key={index} sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                  label={msg.type || 'System'}
                                  size="small"
                                  color={chipColor}
                                  variant={msg.type === 'Error' ? 'filled' : 'outlined'}
                                />
                                <Typography variant="caption" color="textSecondary">
                                  {msg.timestamp}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'pre-wrap',
                                  overflowWrap: 'break-word',
                                  hyphens: 'auto'
                                }}
                              >
                                {msg.content}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}

        {isAnalyzing && !isCompleted && (
          <Grid item xs={12}>
            <Box>
              <LinearProgress />
              <Typography variant="body2" textAlign="center" mt={1} color="textSecondary">
                Analysis in progress... This may take several minutes.
              </Typography>
            </Box>
          </Grid>
        )}
        {isCompleted && (
          <Grid item xs={12}>
            <Box>
              <LinearProgress variant="determinate" value={100} color="success" />
              <Typography variant="body2" textAlign="center" mt={1} color="success.main">
                Analysis complete. Loading results...
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default AnalysisProgress;